import React, { useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { ChevronUp, ChevronDown, MoveUp, MoveDown, PieChartIcon } from 'lucide-react';
import { Button } from '../ui/button';

// Panel move buttons - standalone component
const PanelMoveButtons = ({ panelId, movePanelUp, movePanelDown, isFirst, isLast, lightTheme = false }) => (
  <div className="flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
    <Button
      variant="ghost"
      size="sm"
      onClick={() => movePanelUp(panelId)}
      disabled={isFirst}
      className={`p-1 h-auto ${isFirst ? 'opacity-30 cursor-not-allowed' : lightTheme ? 'text-white/60 hover:text-white hover:bg-white/20' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-200'}`}
      title="Move panel up"
    >
      <MoveUp className="w-4 h-4" />
    </Button>
    <Button
      variant="ghost"
      size="sm"
      onClick={() => movePanelDown(panelId)}
      disabled={isLast}
      className={`p-1 h-auto ${isLast ? 'opacity-30 cursor-not-allowed' : lightTheme ? 'text-white/60 hover:text-white hover:bg-white/20' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-200'}`}
      title="Move panel down"
    >
      <MoveDown className="w-4 h-4" />
    </Button>
  </div>
);

// Color palette for investment types
const INVESTMENT_TYPE_COLORS = {
  'STOCK': '#10b981',      // Emerald
  'BOND': '#3b82f6',       // Blue
  'MUNICIPAL': '#6366f1',  // Indigo
  'BULLION': '#f59e0b',    // Amber/Gold
  'CRYPTO': '#8b5cf6',     // Purple
  'CASH': '#64748b',       // Slate
  'JEWELRY': '#ec4899',    // Pink
  'REAL ESTATE': '#14b8a6', // Teal
  'VEHICLES': '#f97316',   // Orange
  'FIREARMS': '#78716c',   // Stone/Gray
  'PRIVATE INVESTMENTS': '#84cc16', // Lime
};

// Market vs Non-Market vs Cash colors
const MARKET_COLORS = {
  'Market': '#10b981',      // Green
  'Non-Market': '#64748b',  // Gray
  'Cash': '#3b82f6',        // Blue
};

// MUNICIPAL bonds excluded from market assets - they're manually priced
const MARKET_TYPES = ['STOCK', 'BOND', 'BULLION', 'CRYPTO'];
// Cash types to separate out
const CASH_TYPES = ['CASH'];

// Custom tooltip for pie chart - defined outside component to avoid re-creation
const CustomTooltip = ({ active, payload, formatCurrencyPrivate, privacyMode, hideMarketValue }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const isMarket = MARKET_TYPES.includes(data.name);
    const shouldHide = (isMarket && hideMarketValue) || (!isMarket && privacyMode);
    const display = shouldHide ? '•••••' : (formatCurrencyPrivate ? formatCurrencyPrivate(data.value) : data.value.toLocaleString());
    return (
      <div className="bg-slate-800 text-white px-3 py-2 rounded-lg shadow-lg text-sm">
        <p className="font-semibold">{data.name}</p>
        <p>${display}</p>
        <p className="text-slate-300">{data.percent}% of portfolio</p>
        {data.count && <p className="text-slate-400">{data.count} position{data.count > 1 ? 's' : ''}</p>}
      </div>
    );
  }
  return null;
};

// Custom legend - defined outside component
const CustomLegend = ({ payload }) => {
  if (!payload) return null;
  return (
    <div className="flex flex-wrap justify-center gap-2 mt-2">
      {payload.map((entry, index) => (
        <div key={index} className="flex items-center gap-1 text-xs">
          <div 
            className="w-3 h-3 rounded-full" 
            style={{ backgroundColor: entry.color }}
          />
          <span className="text-slate-600">{entry.value}</span>
          <span className="text-slate-400">({entry.payload.percent}%)</span>
        </div>
      ))}
    </div>
  );
};

const PortfolioDiversityPanel = ({
  stocks,
  showDiversity,
  toggleDiversity,
  mobilePreview,
  formatCurrencyPrivate,
  privacyMode,
  hideMarketValue,
  movePanelUp,
  movePanelDown,
  isFirst,
  isLast,
}) => {
  // Mask all values (Total toggle - hides non-market)
  const maskValue = (value) => {
    if (privacyMode) return '•••••';
    return parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Mask market-only values (Market toggle - hides market only)
  const maskMarketValue = (value) => {
    if (hideMarketValue) return '•••••';
    return parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Mask value based on its investment type
  const maskByType = (value, typeName) => {
    const isMarket = MARKET_TYPES.includes(typeName);
    if (isMarket && hideMarketValue) return '•••••';
    if (!isMarket && privacyMode) return '•••••';
    return parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };
  // Calculate diversity data
  const diversityData = useMemo(() => {
    if (!stocks || stocks.length === 0) return null;

    // Non-market asset types where value is stored in currentPrice (shares should be 1)
    const NON_MARKET_TYPES = ['CASH', 'MUNICIPAL', 'JEWELRY', 'REAL ESTATE', 'VEHICLES', 'PRIVATE INVESTMENTS', 'FIREARMS', 'ELECTRONICS', 'ART'];

    // Helper function to get correct value for any asset type
    const getAssetValue = (stock) => {
      const type = stock.investmentType || 'STOCK';
      const shares = stock.shares || 0;
      const currentPrice = stock.currentPrice || 0;
      const symbol = (stock.symbol || '').toUpperCase();
      const isCash = symbol === 'CASH' || type === 'CASH';
      const isNonMarket = NON_MARKET_TYPES.includes(type);

      if (isCash) {
        // CASH: value can be in shares or currentPrice
        if (shares > 1 && currentPrice <= 1) {
          return shares;
        } else if (currentPrice > 1) {
          return currentPrice;
        }
        return shares;
      } else if (isNonMarket) {
        // Non-market: value is in currentPrice, but handle corrupted data
        if (shares > 1 && currentPrice > 1 && shares === currentPrice) {
          // Corrupted: same value in both fields
          return currentPrice;
        } else if (shares > 1 && currentPrice <= 1) {
          // Legacy: value stored in shares
          return shares;
        } else if (currentPrice > 1) {
          // Standard: value in currentPrice
          return currentPrice;
        }
        return shares > 0 ? shares : currentPrice;
      } else {
        // Market assets: shares * currentPrice
        return shares * currentPrice;
      }
    };

    // Group by investment type
    const typeBreakdown = {};
    let totalValue = 0;
    let marketValue = 0;
    let nonMarketValue = 0;
    let cashValue = 0;

    stocks.forEach(stock => {
      // Skip disposed/sold items from totals
      if (stock.disposed) return;
      const type = stock.investmentType || 'STOCK';
      const symbol = (stock.symbol || '').toUpperCase();
      const value = getAssetValue(stock);
      totalValue += value;

      if (!typeBreakdown[type]) {
        typeBreakdown[type] = { value: 0, count: 0 };
      }
      typeBreakdown[type].value += value;
      typeBreakdown[type].count += 1;

      // Categorize into Market, Cash, or Non-Market
      const isCash = CASH_TYPES.includes(type) || symbol === 'CASH';
      if (isCash) {
        cashValue += value;
      } else if (MARKET_TYPES.includes(type)) {
        marketValue += value;
      } else {
        nonMarketValue += value;
      }
    });

    // Convert to chart data format
    const typeChartData = Object.entries(typeBreakdown)
      .map(([type, data]) => ({
        name: type,
        value: data.value,
        count: data.count,
        percent: ((data.value / totalValue) * 100).toFixed(1),
        color: INVESTMENT_TYPE_COLORS[type] || '#94a3b8',
      }))
      .sort((a, b) => b.value - a.value);

    const marketChartData = [
      { 
        name: 'Market', 
        value: marketValue, 
        percent: ((marketValue / totalValue) * 100).toFixed(1),
        color: MARKET_COLORS['Market']
      },
      { 
        name: 'Non-Market', 
        value: nonMarketValue, 
        percent: ((nonMarketValue / totalValue) * 100).toFixed(1),
        color: MARKET_COLORS['Non-Market']
      },
      { 
        name: 'Cash', 
        value: cashValue, 
        percent: ((cashValue / totalValue) * 100).toFixed(1),
        color: MARKET_COLORS['Cash']
      },
    ].filter(d => d.value > 0);

    return {
      typeChartData,
      marketChartData,
      totalValue,
      marketValue,
      nonMarketValue,
      cashValue,
      totalPositions: stocks.length,
    };
  }, [stocks]);

  if (!diversityData) {
    return null;
  }

  return (
    <div className={`${mobilePreview ? 'mb-2' : 'mb-2 sm:mb-4'} scroll-mt-32 sm:scroll-mt-0`}>
      <div className="bg-white rounded-xl shadow-md border-2 border-slate-200 overflow-hidden">
        {/* Mobile Header */}
        <div 
          className="sm:hidden bg-gradient-to-r from-violet-600 to-purple-600 px-3 py-3 cursor-pointer"
          onClick={toggleDiversity}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                <PieChartIcon className="w-4 h-4 text-white" />
              </div>
              <div>
                <h2 className="text-base font-bold text-white">Portfolio Diversity</h2>
                <p className="text-[10px] text-white/70">{diversityData.typeChartData.length} asset types</p>
              </div>
            </div>
            <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-lg text-white">
              {showDiversity ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </div>
          </div>
        </div>

        {/* Desktop Header */}
        <div 
          className="hidden sm:flex bg-gradient-to-r from-violet-600 to-purple-600 px-4 sm:px-6 py-3 sm:py-4 cursor-pointer"
          onClick={toggleDiversity}
        >
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
              <PanelMoveButtons 
                panelId="diversity"
                movePanelUp={movePanelUp}
                movePanelDown={movePanelDown}
                isFirst={isFirst}
                isLast={isLast}
                lightTheme={true}
              />
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <PieChartIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-base sm:text-lg font-bold text-white">Portfolio Diversity</h2>
                <p className="text-xs text-white/70">Asset allocation breakdown</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right hidden md:block">
                <div className="text-xs text-white/70">Total Positions</div>
                <div className="text-lg font-bold text-white">{diversityData.totalPositions}</div>
              </div>
              <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-lg text-white">
                {showDiversity ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        {showDiversity && (
          <div className={`${mobilePreview ? 'p-2' : 'p-4 sm:p-6'}`}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              
              {/* Investment Type Breakdown - Pie Chart */}
              <div className="bg-slate-50 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-slate-700 mb-3 text-center">By Investment Type</h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={diversityData.typeChartData}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {diversityData.typeChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip formatCurrencyPrivate={formatCurrencyPrivate} privacyMode={privacyMode} />} />
                      <Legend content={CustomLegend} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                
                {/* Type breakdown list */}
                <div className="mt-4 space-y-2">
                  {diversityData.typeChartData.map((type, index) => (
                    <div key={index} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: type.color }}
                        />
                        <span className="text-slate-600">{type.name}</span>
                        <span className="text-slate-400 text-xs">({type.count})</span>
                      </div>
                      <div className="text-right">
                        <span className="font-medium text-slate-700">${maskByType(type.value, type.name)}</span>
                        <span className="text-slate-400 ml-2">{type.percent}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Market vs Non-Market - Donut + Bar */}
              <div className="bg-slate-50 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-slate-700 mb-3 text-center">Market vs Non-Market Assets</h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={diversityData.marketChartData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={3}
                        dataKey="value"
                      >
                        {diversityData.marketChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip formatCurrencyPrivate={formatCurrencyPrivate} privacyMode={privacyMode} />} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {/* Market breakdown summary */}
                <div className="mt-4 grid grid-cols-3 gap-3">
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-center">
                    <div className="text-xs text-emerald-600 font-medium mb-1">Market Assets</div>
                    <div className="text-lg font-bold text-emerald-700">
                      ${maskMarketValue(diversityData.marketValue)}
                    </div>
                    <div className="text-xs text-emerald-500">
                      {((diversityData.marketValue / diversityData.totalValue) * 100).toFixed(1)}% of total
                    </div>
                    <div className="text-[10px] text-emerald-400 mt-1">
                      Affects performance %
                    </div>
                  </div>
                  <div className="bg-slate-100 border border-slate-300 rounded-lg p-3 text-center">
                    <div className="text-xs text-slate-600 font-medium mb-1">Non-Market Assets</div>
                    <div className="text-lg font-bold text-slate-700">
                      ${maskValue(diversityData.nonMarketValue)}
                    </div>
                    <div className="text-xs text-slate-500">
                      {((diversityData.nonMarketValue / diversityData.totalValue) * 100).toFixed(1)}% of total
                    </div>
                    <div className="text-[10px] text-slate-400 mt-1">
                      Value only
                    </div>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                    <div className="text-xs text-blue-600 font-medium mb-1">Cash</div>
                    <div className="text-lg font-bold text-blue-700">
                      ${maskValue(diversityData.cashValue)}
                    </div>
                    <div className="text-xs text-blue-500">
                      {((diversityData.cashValue / diversityData.totalValue) * 100).toFixed(1)}% of total
                    </div>
                    <div className="text-[10px] text-blue-400 mt-1">
                      Liquid
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg p-3 text-white text-center">
                <div className="text-xs opacity-80">Total Value</div>
                <div className="text-lg font-bold">${(hideMarketValue && privacyMode) ? '•••••' : maskValue(diversityData.totalValue)}</div>
              </div>
              <div className="bg-gradient-to-br from-emerald-500 to-green-600 rounded-lg p-3 text-white text-center">
                <div className="text-xs opacity-80">Asset Types</div>
                <div className="text-lg font-bold">{diversityData.typeChartData.length}</div>
              </div>
              <div className="bg-gradient-to-br from-blue-500 to-cyan-600 rounded-lg p-3 text-white text-center">
                <div className="text-xs opacity-80">Positions</div>
                <div className="text-lg font-bold">{diversityData.totalPositions}</div>
              </div>
              <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg p-3 text-white text-center">
                <div className="text-xs opacity-80">Largest Type</div>
                <div className="text-lg font-bold">{diversityData.typeChartData[0]?.name || 'N/A'}</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PortfolioDiversityPanel;
